import { View, StyleSheet, Pressable } from 'react-native';
import { useNavigate, useParams } from 'react-router';
import { NavigationList, type NavigationListItemData, Text, Icon, theme } from '@socrait/react';

import { ErrorBox } from '~/components/ErrorBox';
import { MainLayout } from '~/layouts/MainLayout';
import { Period, Student } from '~/services/types';
import { usePeriodListAllQuery } from '~/services/period';
import { Loading } from '~/components/Loading';
import { getStudentDataPath } from '~/screens/studentData';

export function RosterScreen() {
  const { periodShortName } = useParams();
  const navigate = useNavigate();
  const { data, status } = usePeriodListAllQuery();
  const { periods } = data || {};

  const activePeriod = periods?.find((p: Period) => p.shortName === periodShortName) || periods?.[0];

  const closeRoute = activePeriod ? `/review/${activePeriod.shortName}` : '/review';
  const onClose = () => navigate(closeRoute);

  if (status === 'pending') {
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    );
  }

  if (!activePeriod) {
    return (
      <MainLayout>
        <View style={[styles.container, styles.errorContainer]}>
          <Pressable onPress={onClose} style={[styles.closeButton, styles.errorCloseButton]}>
            <Icon type="close" />
          </Pressable>
          <ErrorBox text="Could not load the period data. Please close this window and try again." />
        </View>
      </MainLayout>
    );
  }

  const students = [...(activePeriod.students || [])].sort((a: Student, b: Student) =>
    a.voiceName.localeCompare(b.voiceName),
  );

  const studentItems: NavigationListItemData[] = students.map((student) => ({
    id: student.id,
    label: student.voiceName,
    onPress: () => navigate(getStudentDataPath(activePeriod.shortName, student.voiceName)),
    telemetryLabel: `roster-student-${student.id}`,
  }));

  return (
    <MainLayout>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <Text style={styles.periodName}>{activePeriod.name}</Text>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Icon type="close" />
            </Pressable>
          </View>
          <Text style={styles.studentCount}>
            {students.length} student{students.length !== 1 ? 's' : ''} in roster
          </Text>
        </View>
        <View style={styles.content}>
          <NavigationList containerStyle={styles.studentList} items={studentItems} maxColumns={4} />
        </View>
      </View>
    </MainLayout>
  );
}

const styles = StyleSheet.create({
  errorContainer: {
    padding: theme.spacing.xxlarge,
    gap: theme.spacing.xlarge,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  header: {
    borderBottomColor: theme.colors.gray,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxlarge,
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingTop: theme.spacing.xxlarge,
    paddingBottom: theme.spacing.large,
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  periodName: {
    fontSize: theme.fontSizes.large,
    fontWeight: theme.fontWeights.medium,
  },
  studentCount: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  closeButton: {
    padding: theme.spacing.medium,
  },
  errorCloseButton: {
    alignSelf: 'flex-end',
  },
  studentList: {
    borderRadius: 0,
  },
  content: {
    flex: 1,
    paddingHorizontal: theme.spacing.xlarge * 2,
    overflowY: 'auto',
  },
});
